/**
 * CapacityGridHeader.tsx
 * The sticky column header of the merged "Team Allocation & Weekly Capacity"
 * grid (staffing-allocation follow-up 2026-07-13). Extracted from
 * Phase2CapacityTable to keep it under the 600-line cap.
 *
 * Row 1 = Employee / Role gutter + one "WEEK n" group header per week.
 * Row 2 = Sat→Fri day labels under each week (non-working days dimmed).
 * The hovered week ("hot") gets the shared sage beam, matching the task grid.
 */

import React from 'react';
import { TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { EMP_COL_W, ROLE_COL_W, DAY_W, WEEK_W, DAY_LABELS, weekDays } from './phase2CapacityShared';
import { buildWeekHeaders } from './staffCapacityModel';

export interface CapacityGridHeaderProps {
  weekColumns: string[];
  holidaySet: Set<string>;
  hoveredWeek?: string | null;
  onHoverWeek?: (weekStart: string | null) => void;
}

export const CapacityGridHeader: React.FC<CapacityGridHeaderProps> = ({
  weekColumns,
  holidaySet,
  hoveredWeek,
  onHoverWeek,
}) => {
  const theme = useTheme();
  const beamRail = `2px solid ${theme.schedule.selection}`;

  return (
    <TableHead>
      {/* Row 1 — Employee / Role (spanning) + one group header per WEEK. */}
      <TableRow>
        <TableCell
          rowSpan={2}
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 3,
            backgroundColor: theme.table.headerBg,
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: 0.3,
            width: EMP_COL_W,
            py: 1,
            px: 1.5,
            borderBottom: `2px solid ${theme.border.color}`,
            borderRight: `1px solid ${theme.border.color}`,
            verticalAlign: 'bottom',
          }}
        >
          Employee
        </TableCell>
        <TableCell
          rowSpan={2}
          sx={{
            position: 'sticky',
            left: EMP_COL_W,
            zIndex: 3,
            backgroundColor: theme.table.headerBg,
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: 0.3,
            width: ROLE_COL_W,
            py: 1,
            px: 1.5,
            borderBottom: `2px solid ${theme.border.color}`,
            boxShadow: `inset -1px 0 0 0 ${theme.border.color}, 6px 0 6px -6px ${alpha(theme.palette.text.primary, 0.18)}`,
            whiteSpace: 'nowrap',
            verticalAlign: 'bottom',
          }}
        >
          Role
        </TableCell>
        {buildWeekHeaders(weekColumns).map((wh) => {
          const hot = hoveredWeek === wh.weekStart;
          return (
            <TableCell
              key={wh.weekStart}
              align="center"
              colSpan={7}
              onMouseEnter={() => onHoverWeek?.(wh.weekStart)}
              sx={{
                backgroundColor: hot ? alpha(theme.schedule.selection, 0.14) : theme.table.headerBg,
                py: 0.5,
                px: 0.5,
                width: WEEK_W,
                borderBottom: `1px solid ${theme.border.color}`,
                borderRight: hot ? beamRail : `2px solid ${theme.border.color}`,
                ...(hot ? { borderLeft: beamRail } : {}),
                whiteSpace: 'nowrap',
                transition: 'background-color 0.12s',
              }}
            >
              <Typography variant="caption" display="block" fontWeight={700} sx={{ fontSize: '0.68rem', letterSpacing: 0.5, color: 'text.secondary' }}>
                {wh.numberLabel}
              </Typography>
              <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                {wh.rangeLabel}
              </Typography>
            </TableCell>
          );
        })}
      </TableRow>
      {/* Row 2 — Sat→Fri day labels under each week (non-working days dimmed). */}
      <TableRow>
        {weekColumns.flatMap((ws) => {
          const hot = hoveredWeek === ws;
          return weekDays(ws).map((day, di) => {
            const working = di >= 2 && !holidaySet.has(day);
            return (
              <TableCell
                key={day}
                align="center"
                onMouseEnter={() => onHoverWeek?.(ws)}
                sx={{
                  backgroundColor: hot ? alpha(theme.schedule.selection, 0.08) : theme.table.headerBg,
                  py: 0.25,
                  px: 0,
                  width: DAY_W,
                  borderBottom: `2px solid ${theme.border.color}`,
                  borderRight: di === 6
                    ? (hot ? beamRail : `2px solid ${theme.border.color}`)
                    : `1px solid ${theme.border.color}`,
                  ...(hot && di === 0 ? { borderLeft: beamRail } : {}),
                }}
              >
                <Typography variant="caption" display="block" sx={{ fontSize: '0.77rem', fontWeight: 700, color: working ? 'text.secondary' : 'text.disabled' }}>
                  {DAY_LABELS[di]}
                </Typography>
                <Typography variant="caption" display="block" sx={{ fontSize: '0.68rem', color: 'text.disabled' }}>
                  {parseInt(day.slice(8, 10), 10)}
                </Typography>
              </TableCell>
            );
          });
        })}
      </TableRow>
    </TableHead>
  );
};
